import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { useFocusTrap } from '../_hooks/useFocusTrap';

interface DrawerProps {
    title: string;
    subtitle?: string;
    onClose: () => void;
    canClose?: boolean;
    footer?: ReactNode;
    children: ReactNode;
}

const Drawer = ({ title, subtitle, onClose, canClose = true, footer, children }: DrawerProps) => {
    const { t } = useTranslation();
    const titleId = useId();
    const drawerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    // callers pass onClose as an inline arrow, so keeping it in a ref stops the focus effect re-running and stealing focus mid-edit
    const closeRef = useRef({ onClose, canClose });

    useEffect(() => {
        closeRef.current = { onClose, canClose };
    });

    // declared before the focus trap so it captures the opener, not the control the trap focuses
    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;

        return () => {
            if (previouslyFocused?.isConnected === true) previouslyFocused.focus();
        };
    }, []);

    const { isTopLayer } = useFocusTrap(drawerRef, bodyRef);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || !closeRef.current.canClose) return;

            // a native modal dialog or an open combobox or menu takes the Escape (an expanded section header has no popup to close), and with several drawers open only the newest one closes
            const nativeDialog = document.activeElement?.closest('dialog[open]') ?? null;
            const target = event.target instanceof HTMLElement ? event.target : null;
            const isOpenPopup = target?.getAttribute('aria-expanded') === 'true' && (target.getAttribute('role') === 'combobox' || target.hasAttribute('aria-haspopup'));
            if (nativeDialog !== null || isOpenPopup || !isTopLayer()) return;

            closeRef.current.onClose();
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isTopLayer]);

    return (
        <div className="drawer drawer-right show" ref={drawerRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
            <div className="drawer-content d-flex flex-column h-100">
                <div className="drawer-header pt-0 flex-shrink-0 d-flex justify-content-between align-items-start gap-16 border-bottom">
                    <div>
                        <h4 className="drawer-title mb-0 font-20" id={titleId}>
                            {title}
                        </h4>
                        {subtitle !== undefined && subtitle.length > 0 && <div className="text-secondary font-size-xs mt-4">{subtitle}</div>}
                    </div>
                    <button type="button" className="close close-lg" disabled={!canClose} aria-label={t('common.close') ?? 'Close'} onClick={onClose}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div className="drawer-body flex-1 min-h-0 mb-0 overflow-auto" ref={bodyRef}>
                    {children}
                </div>
                {footer !== undefined && <div className="drawer-footer flex-shrink-0 justify-content-end align-items-center d-flex gap-8">{footer}</div>}
            </div>
        </div>
    );
};

export default Drawer;
